import { Component, inject, computed } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { SidebarComponent, type SidebarItem } from '@echospaces-web-monorepo/es-shared-ui';
import { ToastContainerComponent } from '@echospaces-web-monorepo/es-shared-ui';
import { ThemeService } from '@echospaces-web-monorepo/es-shared-theme';
import { AvatarComponent, ButtonComponent } from '@echospaces-web-monorepo/es-shared-ui';
import { AuthService } from '@echospaces-web-monorepo/es-auth';

import { ACTIVE_COMPANY } from './active-company.token';

const BASE_NAV: SidebarItem[] = [
  { id: 'dashboard', icon: '◈', label: 'Dashboard', route: '/dashboard' },
  { id: 'islands', icon: '🏝', label: 'Islands', route: '/islands' },
  { id: 'content', icon: '📦', label: 'Content', route: '/content' },
];

const USERS_ITEM: SidebarItem = { id: 'users', icon: '👥', label: 'Users', route: '/users' };
const ADMIN_ITEM: SidebarItem = { id: 'admin', icon: '🛡', label: 'Admin', route: '/admin' };
const SETTINGS_ITEM: SidebarItem = { id: 'settings', icon: '⚙', label: 'Settings', route: '/settings' };

const SUPER_ADMIN_ROLE = 'super-admin';

@Component({
  selector: 'es-shell',
  imports: [RouterOutlet, SidebarComponent, ToastContainerComponent, AvatarComponent, ButtonComponent],
  template: `
    <div class="flex h-screen bg-[var(--bg)] overflow-hidden">
      <!-- Sidebar -->
      <es-sidebar
        [items]="navItems()"
        [activeId]="activeNavId()"
        (itemClicked)="navigate($event)"
      ></es-sidebar>

      <!-- Main -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Header -->
        <header class="h-14 flex items-center justify-between px-6 border-b border-[var(--border)] bg-[var(--bg-card)] shrink-0">
          <h1 class="text-lg font-semibold text-[var(--text)]">{{ pageTitle() }}</h1>

          <div class="flex items-center gap-3">
            <!-- Active company badge — which company this BFF is bound to
                 (anti-mix-up in local CLI dev; localhost = the env-selected main company) -->
            @if (company; as c) {
              <span
                class="text-xs font-mono px-2 py-1 rounded-md border border-[var(--border)] bg-[var(--bg)] text-[var(--text-secondary)]"
                [title]="'Company organizationId: ' + c.organizationId"
              >
                ● {{ c.resourceId }}
              </span>
            }

            <!-- Theme toggle -->
            <button
              class="text-[var(--text-secondary)] hover:text-[var(--text)] cursor-pointer text-sm"
              (click)="theme.toggle()"
            >
              {{ theme.darkMode() ? '☀' : '🌙' }}
            </button>

            <!-- User -->
            @if (auth.user(); as user) {
              <es-avatar [name]="user.username" size="sm"></es-avatar>
              <span class="text-sm text-[var(--text-secondary)]">{{ user.username }}</span>
            }

            <es-button variant="ghost" size="sm" (clicked)="auth.logout()">
              Logout
            </es-button>
          </div>
        </header>

        <!-- Page content -->
        <main class="flex-1 overflow-auto p-6">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>

    <!-- Global toast notifications -->
    <es-toast-container></es-toast-container>
  `,
})
export class ShellComponent {
  readonly auth = inject(AuthService);
  readonly theme = inject(ThemeService);
  private readonly router = inject(Router);

  /** The company this BFF is bound to — badged in the header. Null when unknown. */
  readonly company = inject(ACTIVE_COMPANY, { optional: true });

  /**
   * Sidebar items. `Users` (member management) and `Admin` are only listed
   * when the JWT carries the `super-admin` realm role — both are otherwise
   * invisible. Settings stays for everyone (user profile + personal preferences).
   */
  readonly navItems = computed<SidebarItem[]>(() => {
    const items = [...BASE_NAV];
    if (this.auth.user()?.roles.includes(SUPER_ADMIN_ROLE)) {
      items.push(USERS_ITEM, ADMIN_ITEM);
    }
    items.push(SETTINGS_ITEM);
    return items;
  });

  activeNavId = computed(() => {
    const url = this.router.url;
    const match = this.navItems().find(item => item.route && url.startsWith(item.route));
    return match?.id ?? 'dashboard';
  });

  pageTitle = computed(() => {
    const active = this.navItems().find(i => i.id === this.activeNavId());
    return active?.label ?? 'EchoSpaces';
  });

  navigate(item: SidebarItem) {
    if (item.route) {
      this.router.navigate([item.route]);
    }
  }
}
